import { create } from 'zustand';
import { Step } from 'react-joyride';

import { StorageAPI } from '@/hooks/useStorage.ts';
import { STORAGE_KEYS } from '@/constants/local_storage_keys.ts';

interface TourProgress {
  completedSteps: string[];
  isCompleted: boolean;
}

interface JoyrideTourStore {
  run: boolean;
  stepIndex: number;
  steps: Step[];
  originalSteps: Step[]; // Original steps with string selectors
  originalStepKeys: string[]; // Original string selectors for tracking
  currentTourKey: string | null;
  storage: StorageAPI | null;

  startTour: (
    tourKey: string,
    steps: Step[],
    storage: StorageAPI
  ) => Promise<void>;
  stopTour: () => void;
  pauseTour: () => void;
  nextStep: () => void;
  resetTour: (tourKey: string, storage: StorageAPI) => void;
}

const getStorageKey = (tourKey: string) =>
  `${STORAGE_KEYS.JOYRIDE_TOUR}_${tourKey}`;

const getStepKey = (step: Step): string => {
  return typeof step.target === 'string' ? step.target : '';
};

// Get the last matching element (front card is rendered last in DOM)
const getLastMatchingElement = (selector: string): HTMLElement | null => {
  const elements = document.querySelectorAll<HTMLElement>(selector);
  return elements.length > 0 ? elements[elements.length - 1] : null;
};

// Transform steps to use last matching element for proper positioning
const transformStepsWithLastElement = (steps: Step[]): Step[] => {
  return steps.map((step) => {
    if (typeof step.target === 'string') {
      const element = getLastMatchingElement(step.target);
      if (element) {
        return { ...step, target: element };
      }
    }
    return step;
  });
};

const readProgress = async (
  storage: StorageAPI,
  storageKey: string
): Promise<TourProgress | null> => {
  return storage.readData<TourProgress>(storageKey);
};

export const useJoyrideTourStore = create<JoyrideTourStore>((set, get) => ({
  run: false,
  stepIndex: 0,
  steps: [],
  originalSteps: [],
  originalStepKeys: [],
  currentTourKey: null,
  storage: null,

  startTour: async (tourKey, steps, storage) => {
    const storageKey = getStorageKey(tourKey);
    const progress = await readProgress(storage, storageKey);

    if (progress?.isCompleted) {
      return;
    }

    // Save original string selectors for tracking
    const stepKeys = steps.map(getStepKey);

    const completedSteps = progress?.completedSteps || [];
    const firstIncompleteIndex = steps.findIndex(
      (step) => !completedSteps.includes(getStepKey(step))
    );

    if (firstIncompleteIndex === -1) {
      storage.writeData(storageKey, {
        completedSteps,
        isCompleted: true
      });
      return;
    }

    // Transform steps to use last matching elements (front card is rendered last)
    const transformedSteps = transformStepsWithLastElement(steps);

    set({
      run: true,
      stepIndex: firstIncompleteIndex,
      steps: transformedSteps,
      originalSteps: steps,
      originalStepKeys: stepKeys,
      currentTourKey: tourKey,
      storage
    });
  },

  stopTour: () => {
    const { currentTourKey, stepIndex, storage, originalStepKeys } = get();

    if (currentTourKey && storage) {
      const storageKey = getStorageKey(currentTourKey);
      void readProgress(storage, storageKey).then((progress) => {
        const completedSteps = progress?.completedSteps || [];
        const newCompletedSteps = [...completedSteps];
        // Mark all steps up to and including current step as completed
        for (let i = 0; i <= stepIndex; i++) {
          const stepKey = originalStepKeys[i];
          if (stepKey && !newCompletedSteps.includes(stepKey)) {
            newCompletedSteps.push(stepKey);
          }
        }
        storage.writeData(storageKey, {
          completedSteps: newCompletedSteps,
          isCompleted: false
        });
      });
    }

    set({
      run: false,
      stepIndex: 0,
      steps: [],
      originalSteps: [],
      originalStepKeys: [],
      currentTourKey: null,
      storage: null
    });
  },

  pauseTour: () => {
    set({ run: false });
  },

  nextStep: () => {
    const {
      stepIndex,
      steps,
      originalSteps,
      currentTourKey,
      storage,
      originalStepKeys
    } = get();
    const nextIndex = stepIndex + 1;

    if (currentTourKey && storage) {
      const storageKey = getStorageKey(currentTourKey);
      const currentStepKey = originalStepKeys[stepIndex];

      void readProgress(storage, storageKey).then((progress) => {
        const completedSteps = progress?.completedSteps || [];
        if (currentStepKey && !completedSteps.includes(currentStepKey)) {
          completedSteps.push(currentStepKey);
        }

        const isCompleted = nextIndex >= steps.length;
        storage.writeData(storageKey, {
          completedSteps,
          isCompleted
        });
      });
    }

    if (nextIndex >= steps.length) {
      set({
        run: false,
        stepIndex: 0,
        steps: [],
        originalSteps: [],
        originalStepKeys: [],
        currentTourKey: null,
        storage: null
      });
    } else {
      // Re-transform steps to get fresh HTMLElement references
      const freshSteps = transformStepsWithLastElement(originalSteps);
      set({ run: true, stepIndex: nextIndex, steps: freshSteps });
    }
  },

  resetTour: (tourKey, storage) => {
    const storageKey = getStorageKey(tourKey);
    storage.removeData(storageKey);
    set({
      run: false,
      stepIndex: 0,
      steps: [],
      originalSteps: [],
      originalStepKeys: [],
      currentTourKey: null,
      storage: null
    });
  }
}));
